import { useId, useMemo } from "react";

import type { AeroLiftingLineSolveResponse } from "../../api/aeroWorkbench";

const WIDTH = 760;
const HEIGHT = 280;
const MARGIN = { top: 22, right: 24, bottom: 42, left: 66 } as const;

export interface LiftDistributionPoint {
  eta: number;
  liftPerSpan: number;
  circulation: number;
  outsideDomain: boolean;
}

export function liftDistributionPoints(response: AeroLiftingLineSolveResponse): LiftDistributionPoint[] {
  const filamentByPanel = new Map(response.wake_filaments.map((filament) => [filament.panel_id, filament]));
  const halfSpan = Math.max(
    1e-9,
    ...response.wake_filaments.flatMap((filament) => [
      Math.abs(filament.bound_start_m[1]),
      Math.abs(filament.bound_end_m[1]),
    ]),
  );
  return response.result.span_loads
    .flatMap((load) => {
      const filament = filamentByPanel.get(load.panel_id);
      if (!filament) return [];
      const startY = filament.bound_start_m[1];
      const endY = filament.bound_end_m[1];
      const bodyYWidth = Math.abs(endY - startY);
      if (![startY, endY, bodyYWidth].every(Number.isFinite) || bodyYWidth <= 1e-12) return [];
      return [{
        eta: 0.5 * (startY + endY) / halfSpan,
        liftPerSpan: load.lift_n / bodyYWidth,
        circulation: load.circulation_m2_s,
        outsideDomain: load.prediction?.metadata?.outside_training_domain === true,
      }];
    })
    .sort((a, b) => a.eta - b.eta);
}

function ticks(lo: number, hi: number, count = 5): number[] {
  if (!(hi > lo)) return [lo];
  return Array.from({ length: count }, (_, index) => lo + (hi - lo) * index / (count - 1));
}

export function LiftDistributionPlot({ response }: { response: AeroLiftingLineSolveResponse }) {
  const titleId = useId();
  const gradientId = useId().replace(/:/g, "");
  const points = useMemo(() => liftDistributionPoints(response), [response]);
  const plotWidth = WIDTH - MARGIN.left - MARGIN.right;
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const minLift = Math.min(0, ...points.map((point) => point.liftPerSpan));
  const maxLift = Math.max(0, ...points.map((point) => point.liftPerSpan));
  const liftSpan = Math.max(1e-9, maxLift - minLift);
  const yMin = minLift - liftSpan * 0.08;
  const yMax = maxLift + liftSpan * 0.08;
  const x = (eta: number) => MARGIN.left + ((eta + 1) / 2) * plotWidth;
  const y = (lift: number) => MARGIN.top + ((yMax - lift) / (yMax - yMin)) * plotHeight;
  const zeroY = y(0);
  const linePath = points.map((point, index) => `${index ? "L" : "M"}${x(point.eta).toFixed(2)} ${y(point.liftPerSpan).toFixed(2)}`).join(" ");
  const areaPath = points.length
    ? `${linePath} L${x(points[points.length - 1]!.eta).toFixed(2)} ${zeroY.toFixed(2)} L${x(points[0]!.eta).toFixed(2)} ${zeroY.toFixed(2)} Z`
    : "";
  const peak = Math.max(0, ...points.map((point) => point.liftPerSpan));
  const peakGamma = Math.max(0, ...points.map((point) => Math.abs(point.circulation)));
  const outsideCount = points.filter((point) => point.outsideDomain).length;

  return (
    <section className="lift-distribution" aria-labelledby={titleId} data-testid="lift-distribution-plot">
      <header>
        <span>
          <strong id={titleId}>Spanwise lift distribution</strong>
          <small>computed relaxed-wake panel loads · L′ = panel lift / |Δbody-y|</small>
        </span>
        <dl>
          <div><dt>peak L′</dt><dd>{peak.toLocaleString(undefined, { maximumFractionDigits: 0 })} N/m</dd></div>
          <div><dt>peak |Γ|</dt><dd>{peakGamma.toFixed(2)} m²/s</dd></div>
          <div><dt>panels</dt><dd>{points.length}</dd></div>
        </dl>
      </header>
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label="Lift per unit span across the wing">
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="var(--fc-primary-bright)" stopOpacity="0.34" />
            <stop offset="1" stopColor="var(--fc-primary-bright)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <rect x={MARGIN.left} y={MARGIN.top} width={plotWidth} height={plotHeight} fill="var(--fc-surface-2)" stroke="var(--fc-border)" />
        {ticks(yMin, yMax).map((tick) => (
          <g key={tick}>
            <line x1={MARGIN.left} x2={MARGIN.left + plotWidth} y1={y(tick)} y2={y(tick)} stroke="var(--fc-border)" strokeWidth="0.75" />
            <text x={MARGIN.left - 8} y={y(tick) + 3} textAnchor="end">{tick.toLocaleString(undefined, { maximumFractionDigits: 0 })}</text>
          </g>
        ))}
        {[-1, -0.5, 0, 0.5, 1].map((eta) => (
          <g key={eta}>
            <line x1={x(eta)} x2={x(eta)} y1={MARGIN.top} y2={MARGIN.top + plotHeight} stroke="var(--fc-border)" strokeWidth={eta === 0 ? 1 : 0.5} />
            <text x={x(eta)} y={HEIGHT - 20} textAnchor="middle">{eta.toFixed(1)}</text>
          </g>
        ))}
        {areaPath ? <path d={areaPath} fill={`url(#${gradientId})`} /> : null}
        {linePath ? <path d={linePath} fill="none" stroke="var(--fc-primary-bright)" strokeWidth="2" /> : null}
        {points.map((point) => (
          <circle
            key={`${point.eta}-${point.liftPerSpan}`}
            cx={x(point.eta)}
            cy={y(point.liftPerSpan)}
            r={point.outsideDomain ? 3.5 : 2.25}
            fill={point.outsideDomain ? "var(--fc-warning)" : "var(--fc-surface)"}
            stroke={point.outsideDomain ? "var(--fc-warning)" : "var(--fc-primary-bright)"}
          />
        ))}
        <text x={MARGIN.left + plotWidth / 2} y={HEIGHT - 4} textAnchor="middle">body span η = y / (bᵧ/2)</text>
        <text x={14} y={MARGIN.top + plotHeight / 2} textAnchor="middle" transform={`rotate(-90 14 ${MARGIN.top + plotHeight / 2})`}>lift per span L′ (N/m)</text>
      </svg>
      <footer>
        <span><i /> relaxed-wake result</span>
        {outsideCount ? <em>{outsideCount} model-extrapolated {outsideCount === 1 ? "panel" : "panels"}</em> : <em>all section closures inside aircraft model domain</em>}
      </footer>
    </section>
  );
}
